import * as React from 'react';
import * as Classes from '../common/classes';
import { Icon } from '../icon/Icon';
import { Popover } from '@mc/react-core';
import { Menu } from './Menu';
import classNames from 'classnames';
import { mcNavigateNext } from '@mc/react-ui-icons';

import type { IPopoverProps, PopperModifiers } from '@mc/react-core';
import type { MenuItemIntent } from '../common/props';

interface Props extends Omit<React.HTMLAttributes<HTMLLIElement>, 'label'> {
  /**
   * Text položky
   */
  text: React.ReactNode;
  /**
   * Typ informace.
   */
  intent?: MenuItemIntent;
  icon?: React.ReactNode;
  /**
   * Zda by se tato položka měla zobrazit jako aktivní.
   */
  active?: boolean;
  /**
   * Zda je item interaktivní.
   */
  disabled?: boolean;
  popoverProps?: Partial<IPopoverProps>;
  /**
   * Název tagu, který obaluje obsah MenuItemu.
   *
   * @default a
   */
  tagName?: keyof JSX.IntrinsicElements;
  /**
   * Classa, která se předá textu itemu.
   */
  textClassName?: string;
  /**
   * HTML titulek.
   */
  htmlTitle?: string;
  children?: React.ReactNode;
}

/**
 * MenuItem - typicky pro dropdown menu.
 */
const MenuItem: React.FC<Props> = React.forwardRef<HTMLLIElement, Props>(
  (
    {
      text,
      intent,
      icon,
      active = false,
      disabled = false,
      popoverProps,
      tagName = 'a',
      textClassName,
      htmlTitle,
      className,
      children,
      ...restProps
    },
    ref
  ) => {
    const hasSubMenu = !!children;
    const liClassName = classNames({
      [Classes.MENU_ITEM_SUBMENU]: hasSubMenu,
    });
    const targetClassName = classNames(
      Classes.MENU_ITEM,
      {
        [Classes.ACTIVE]: active,
        [Classes.MENU_ITEM_DISABLED]: disabled,
        [Classes.TYPOGRAPHY_DANGER]: intent === 'danger',
        [Classes.TYPOGRAPHY_SUCCESS]: intent === 'success',
        [Classes.TYPOGRAPHY_WARNING]: intent === 'warning',
        [Classes.TYPOGRAPHY_DEFAULT]: !intent || intent === 'default',
      },
      className
    );
    const target = React.createElement(
      tagName,
      {
        ...restProps,
        ...(disabled ? DISABLED_PROPS : {}),
        className: targetClassName,
      },
      <div className={Classes.MENU_ITEM_CONTENT}>
        <div className={Classes.MENU_ITEM_ICON}>{icon}</div>
        <div
          className={classNames(Classes.MENU_ITEM_TEXT, textClassName)}
          title={htmlTitle}
        >
          {text}
        </div>
      </div>,
      hasSubMenu ? (
        <Icon
          className={Classes.MENU_ITEM_CHEVRON}
          icon={mcNavigateNext}
          style={{
            marginLeft: 17,
          }}
        />
      ) : null
    );
    return (
      <li ref={ref} className={liClassName}>
        {!children ? (
          target
        ) : (
          <Popover
            autoFocus={false}
            captureDismiss={false}
            disabled={disabled}
            enforceFocus={false}
            hoverCloseDelay={0}
            interactionKind={'hover'}
            modifiers={SUBMENU_POPOVER_MODIFIERS}
            position={'right-top'}
            usePortal={false}
            {...popoverProps}
            content={<Menu>{children}</Menu>}
            minimal={true}
            popoverClassName={classNames(
              Classes.MENU_ITEM_SUBMENU,
              popoverProps?.popoverClassName
            )}
            target={target}
          />
        )}
      </li>
    );
  }
);

const DISABLED_PROPS: React.AnchorHTMLAttributes<HTMLAnchorElement> = {
  href: undefined,
  onClick: undefined,
  onMouseDown: undefined,
  onMouseEnter: undefined,
  onMouseLeave: undefined,
  tabIndex: -1,
};

const SUBMENU_POPOVER_MODIFIERS: PopperModifiers = {
  flip: { boundariesElement: 'viewport', padding: 20 },
  offset: { offset: -5 },
  preventOverflow: { boundariesElement: 'viewport', padding: 20 },
};

export { MenuItem };
